import React, { useState, useContext, useEffect, useRef } from "react";
import axios from "axios";
import SimpleButton from "../../../../components/SimpleButton/SimpleButton";
import "./style/FilterProjects.scss";
import AttachmentIcon from "../../../../components/VectorIcons/AttachmentIcon";
import NewActivityIcon from "../../../../components/VectorIcons/NewActivityIcon";
import AppContext from "../../../../context/appContext";
import SelectContent from "../../../../components/SelectContent/SelectContent";
import { useDebounce } from "../../../../hooks/useDebounce";
import { searchContents } from "../../../../services/api/contents/contents.api";

const SEARCH_TYPES = ["Dramaturgy", "Variety", "Research", "LiteraryWork"];
// Maximum PageSize accepted by the ContentSearch API.
const SEARCH_PAGE_SIZE = 100;

const FilterProjectsForActivityCreation = (props) => {
  const { getCurrentItem, currentId, showAttachments, clearFiles } = props;

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentProject, setCurrentProject] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 800);
  const currentIdRef = useRef(currentId);
  useEffect(() => {
    currentIdRef.current = currentId;
  }, [currentId]);

  const appContext = useContext(AppContext);

  // Fetch projects whenever the debounced search term changes.
  useEffect(() => {
    if (!debouncedSearchInput || debouncedSearchInput.length <= 1) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    const source = axios.CancelToken.source();
    setIsLoading(true);

    searchContents(
      {
        Term: debouncedSearchInput,
        Types: SEARCH_TYPES,
        PageSize: SEARCH_PAGE_SIZE,
      },
      { cancelToken: source.token }
    )
      .then((response) => {
        const collection = response.items.items.map((data) => ({
          value: data.id,
          label: data.title,
          ...data,
        }));

        const query = debouncedSearchInput.toLowerCase();
        const exactMatches = collection.filter(
          (item) => item.title.toLowerCase() === query
        );
        const exactIds = new Set(exactMatches.map((item) => item.id));
        const remaining = collection.filter((item) => !exactIds.has(item.id));

        const result = [...exactMatches, ...remaining].filter(
          (item) => item.id !== currentIdRef.current
        );

        setOptions(result);
        setIsLoading(false);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.error("Error fetching projects:", error);
        setOptions([]);
        setIsLoading(false);
      });

    return () => {
      source.cancel();
    };
  }, [debouncedSearchInput]);

  // Update the search term on every keystroke; useDebounce throttles the fetch.
  const handleInputChange = (inputValue, { action }) => {
    if (action !== "input-change") return;
    setSearchInput(inputValue);
    setIsLoading(inputValue.length > 1);
  };

  // Clear results and search term when the field loses focus.
  const handleBlur = () => {
    setSearchInput("");
    setOptions([]);
    setIsLoading(false);
  };

  const handleChangeProjects = (item) => {
    clearFiles();
    showAttachments(false);
    setCurrentProject(item ?? "");
    if (item) getCurrentItem(item);
  };

  return (
    <div className="filterProjectsContainer">
      <SelectContent
        options={options}
        isLoading={isLoading}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        onChange={handleChangeProjects}
        classNamePrefix="filterProjects"
        noOptionsMessage="Nenhum item encontrado"
        loadingMessage="Carregando..."
        placeholder="Digite um título"
      />
      {currentProject.type === "Research" ||
      currentProject.type === "LiteraryWork" ? (
        <SimpleButton
          text="Criar atividade"
          classButton="buttonBlue"
          dataQA="button-prospect-edit"
          isValidForm={!!currentProject}
          toValidate={true}
          clickEvent={() =>
            appContext.setCurrentStepBox("createResearchActivity")
          }
        >
          <NewActivityIcon />
        </SimpleButton>
      ) : (
        <SimpleButton
          text="Listar anexos"
          classButton="buttonBlue"
          dataQA="button-prospect-edit"
          isValidForm={!!currentProject}
          toValidate={true}
          clickEvent={() => showAttachments(true)}
        >
          <AttachmentIcon />
        </SimpleButton>
      )}
    </div>
  );
};

export default FilterProjectsForActivityCreation;
